import { BookOpenIcon, CircleDotIcon } from "lucide-react"
import { useEffect, useState } from "react"
import { usePostHog } from "posthog-js/react"
import { posthogEnabled } from "~/components/providers/Posthog"
import { authClient } from "~/lib/auth-client"
import { useLocation } from "react-router-dom"
import { Link } from "~/components/Link"
import {
	SidebarGroup,
	SidebarGroupLabel,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem
} from "~/components/ui/sidebar"
import { useOrganization } from "~/hooks/useOrganization"
import { api, type RouterOutputs } from "~/trpc/react"

export function NavFavorites() {
	const { organization } = useOrganization()
	const { data: session } = authClient.useSession()
	const favorites = api.favorites.list.useQuery(
		{ organizationId: organization?.id ?? "" },
		{ enabled: !!organization?.id }
	)
	if (!favorites.data?.length || !session || !organization) return null
	const storageKey = `orbit-favorites-order:v1:${session.user.id}:${organization.id}`
	return (
		<FavoriteList
			key={storageKey}
			storageKey={storageKey}
			favorites={favorites.data}
		/>
	)
}

function FavoriteList({
	storageKey,
	favorites
}: {
	storageKey: string
	favorites: RouterOutputs["favorites"]["list"]
}) {
	const location = useLocation()
	const posthog = usePostHog()
	const [order, setOrder] = useState<string[]>(() => {
		try {
			const stored: unknown = JSON.parse(
				localStorage.getItem(storageKey) ?? "[]"
			)
			return Array.isArray(stored)
				? stored.filter((id): id is string => typeof id === "string")
				: []
		} catch {
			return []
		}
	})
	const [draggedId, setDraggedId] = useState<string | null>(null)
	const [dropTarget, setDropTarget] = useState<{
		id: string
		after: boolean
	} | null>(null)
	useEffect(() => {
		try {
			localStorage.setItem(storageKey, JSON.stringify(order))
		} catch {
			// Reordering remains usable when browser storage is unavailable.
		}
	}, [order, storageKey])
	const positions = new Map(order.map((id, index) => [id, index]))
	const sorted = [...favorites].sort(
		(a, b) =>
			(positions.get(`${a.kind}-${a.id}`) ?? -1) -
			(positions.get(`${b.kind}-${b.id}`) ?? -1)
	)
	return (
		<SidebarGroup>
			<SidebarGroupLabel variant="section">Favourites</SidebarGroupLabel>
			<SidebarMenu>
				{sorted.map((favorite, index) => {
					const id = `${favorite.kind}-${favorite.id}`
					return (
						<SidebarMenuItem
							key={id}
							dragging={draggedId === id}
							dropPosition={
								dropTarget?.id === id
									? dropTarget.after
										? "after"
										: "before"
									: undefined
							}
							onDragOver={(event) => {
								if (!draggedId) return
								event.preventDefault()
								if (draggedId === id) return
								event.dataTransfer.dropEffect = "move"
								const rect = event.currentTarget.getBoundingClientRect()
								setDropTarget({
									id,
									after: event.clientY > rect.top + rect.height / 2
								})
							}}
							onDragLeave={(event) => {
								if (
									!(event.relatedTarget instanceof Node) ||
									!event.currentTarget.contains(event.relatedTarget)
								)
									setDropTarget(null)
							}}
							onDrop={(event) => {
								if (!draggedId) return
								event.preventDefault()
								if (draggedId === id) return
								const ids = sorted.map((item) => `${item.kind}-${item.id}`)
								if (!ids.includes(draggedId)) return
								const next = ids.filter((itemId) => itemId !== draggedId)
								const rect = event.currentTarget.getBoundingClientRect()
								next.splice(
									next.indexOf(id) +
										Number(event.clientY > rect.top + rect.height / 2),
									0,
									draggedId
								)
								setOrder(next)
								setDraggedId(null)
								setDropTarget(null)
								if (posthogEnabled)
									posthog?.capture("favorites_reordered", { method: "drag" })
							}}
						>
							<SidebarMenuButton
								asChild
								tooltip={favorite.title}
								isActive={
									`${location.pathname}${location.search}` === favorite.href
								}
							>
								<Link
									to={favorite.href}
									draggable
									aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown"
									onDragStart={(event) => {
										event.dataTransfer.effectAllowed = "move"
										event.dataTransfer.setData("text/plain", id)
										setDraggedId(id)
									}}
									onDragEnd={() => {
										setDraggedId(null)
										setDropTarget(null)
									}}
									onKeyDown={(event) => {
										if (
											!event.altKey ||
											!["ArrowUp", "ArrowDown"].includes(event.key)
										)
											return
										event.preventDefault()
										const targetIndex =
											index + (event.key === "ArrowDown" ? 1 : -1)
										if (targetIndex < 0 || targetIndex >= sorted.length) return
										const next = sorted.map((item) => `${item.kind}-${item.id}`)
										next.splice(index, 1)
										next.splice(targetIndex, 0, id)
										setOrder(next)
										if (posthogEnabled)
											posthog?.capture("favorites_reordered", {
												method: "keyboard"
											})
									}}
								>
									{favorite.kind === "document" ? (
										<BookOpenIcon className="size-4" />
									) : (
										<CircleDotIcon className="size-4" />
									)}
									<span className="truncate">{favorite.title}</span>
								</Link>
							</SidebarMenuButton>
						</SidebarMenuItem>
					)
				})}
			</SidebarMenu>
		</SidebarGroup>
	)
}
